import { useAuth0 } from '@auth0/auth0-react';
import {
  CLAIM_AIRLINE,
  CLAIM_OPERATOR_URN,
  CLAIM_ROLE,
  type Role,
  type ScopeEntry,
} from '@nexa/auth';
import { useCachedAccessSnapshot } from '@/lib/access/snapshot';
import { airportsOfScope } from '@/lib/access/resolve';
import { useSessionClaims } from './session-claims';

// Typed reader for Nexa custom claims per docs/ui/auth0-frontend-tds.md §5.4
// and docs/ui/operator-ui-tds.md §3.2. Mirrors apps/admin-ui's contract — never
// read raw claim keys at call sites, only via this hook (the claim parsing itself lives in
// `session-claims.ts`, whose tolerant hooks serve chrome and access code).
//
// users-roles-permissions R5 — the TOKEN is no longer the whole answer:
//
//   - **Role.** A member may sign in with no `https://nexa/role` claim (default-deny moved
//     to the BFF, brief §4.19), and the role today's guards evaluate is DERIVED by the BFF
//     from the operator's roles (`GET /v1/me@v2`, M1 §8.4). So `role` prefers the cached
//     `/v1/me` answer and falls back to the claim only while that is unknown. The read is
//     passive — this hook never fetches.
//   - **Scope.** The claim may be a LIST, normalized fail-closed (`normalizeScopeEntries`):
//     absent → tenant-wide, `[]` → no airports, entries → bounded, malformed → no airports.
//     The BFF's `access.scope` / `airports` win when `/v1/me` has them.
//
// INVARIANT: every hook runs BEFORE any throw, so the hook order is the same whether or not
// it throws — `<RoleGate>` relies on catching the throw to read "no role" safely.

export interface Session {
  /** Airline URN, e.g. `urn:airline:latam`. */
  airline: string;
  /** Legacy role for `hasRole()` call sites (M1). Prefer `useAccess().can(permission)`. */
  role: Role;
  /** Stable operator identity, e.g. `urn:user:auth0|...`. */
  operatorUrn: string;
  /** Display name from the ID token (falls back to email or sub). */
  displayName: string;
  /** The operator's scope list; tenant-wide is `[{ kind: 'ALL' }]`, none is `[]`. */
  scope: readonly ScopeEntry[];
  /** Effective airports: `'*'` = the whole airline, `[]` = none. */
  airports: readonly string[] | '*';
}

export function useSession(): Session {
  const { isAuthenticated } = useAuth0();
  const claims = useSessionClaims();
  const { snapshot } = useCachedAccessSnapshot(claims?.scope ?? null);
  if (!isAuthenticated || !claims) {
    throw new Error(
      'useSession() called before authentication. Mount inside withAuthenticationRequired.',
    );
  }

  const { airline, operatorUrn, displayName } = claims;
  if (!airline) {
    throw new Error(`Missing or invalid claim ${CLAIM_AIRLINE} on user`);
  }
  if (!operatorUrn) {
    throw new Error(`Missing or invalid claim ${CLAIM_OPERATOR_URN} on user`);
  }
  const role = snapshot ? snapshot.legacyRole : claims.role;
  if (!role) {
    // Unreachable under the access boundary, which renders "Sin acceso asignado" instead
    // of any route for a member with no role. Chrome uses `useSessionRole()`.
    throw new Error(`No role for this session (claim ${CLAIM_ROLE} absent and no access assigned)`);
  }

  const scope = snapshot ? snapshot.scope : (claims.scope ?? [{ kind: 'ALL' as const }]);
  const airports = snapshot ? snapshot.airports : airportsOfScope(claims.scope);
  return { airline, role, operatorUrn, displayName, scope, airports };
}
